import React, { useEffect, useState } from 'react';
import { checkConnection } from '../lib/supabase';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';

export function ConnectionStatus() {
  const [status, setStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  async function verifyConnection() {
    const { success, error } = await checkConnection();
    if (success) {
      setStatus('connected');
      // Auto-hide after 5 seconds if connected
      setTimeout(() => {
        setVisible(false);
      }, 5000);
    } else {
      setStatus('error');
      setError(error instanceof Error ? error.message : 'Connection failed');
      
      // Retry logic
      if (retryCount < maxRetries) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          setStatus('checking');
          verifyConnection();
        }, 2000 * (retryCount + 1)); // Exponential backoff
      }
    }
  }

  useEffect(() => {
    verifyConnection();
  }, []);

  const handleRetry = () => {
    setStatus('checking');
    setRetryCount(0);
    verifyConnection();
  };

  if (!visible) {
    return null;
  }

  if (status === 'checking') {
    return (
      <div className="fixed bottom-4 right-4 bg-cream-100 p-3 md:p-4 rounded-lg shadow-lg z-40">
        <p className="text-primary-600 flex items-center text-sm">
          <RefreshCw className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" />
          {retryCount > 0 ? `Retrying connection (${retryCount}/${maxRetries})...` : 'Checking connection...'}
        </p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="fixed bottom-4 right-4 bg-red-50 p-3 md:p-4 rounded-lg shadow-lg z-40">
        <div className="flex flex-col gap-2">
          <p className="text-red-600 flex items-center text-sm">
            <XCircle className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
            <span>{error}</span>
          </p>
          {retryCount >= maxRetries && (
            <button
              onClick={handleRetry}
              className="text-red-600 text-sm flex items-center hover:text-red-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry Connection
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-green-50 p-3 md:p-4 rounded-lg shadow-lg opacity-100 transition-opacity duration-1000 hover:opacity-0 z-40">
      <p className="text-green-600 flex items-center text-sm">
        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 mr-2" />
        Connected to Supabase
      </p>
    </div>
  );
}